import { CDN_MODULES, DEFAULT_TOKEN_ICON, forgeCDNUrl } from "../cdn-client";
import { isDexId } from "../cexs/constants";
import { DEX_PROTOCOL_TO_LOGO, extractDexProtocol } from "../dex";
import { EXCHANGE_LOGOS } from "./logo";

// Re-export CEX logos for direct access if needed
export { EXCHANGE_LOGOS as CEX_LOGOS };

// Re-export isDexId for backwards compatibility
export { isDexId };

/**
 * Extract DEX protocol from DEX ID (e.g., "aerodrome-cl" from "aerodrome-cl|base", "pancake-v3" from "pancake-v3|arbitrum")
 */
export const getDexProtocol = extractDexProtocol;

/**
 * Get the logo URL for a DEX
 * @param dexId - DEX ID in format {protocol}|{chain} (e.g., "aerodrome-cl|base", "pancake-v3|arbitrum")
 * @returns URL path to the DEX logo (e.g., "/dexs/aerodrome.svg")
 */
export const getDexLogo = (dexId: string): string => {
  const protocol = getDexProtocol(dexId);
  if (!protocol) return "";
  const logoName = DEX_PROTOCOL_TO_LOGO[protocol];
  if (logoName) {
    return forgeCDNUrl(CDN_MODULES.DEX, `${logoName}.svg`);
  }
  // Fallback: try using the protocol name directly (strip suffixes like -cl, -v3)
  const baseName = protocol.replace(/-cl$|-v\d+$/, "");
  return forgeCDNUrl(CDN_MODULES.DEX, `${baseName}.svg`);
};

/**
 * Get the logo for a CEX as a data URL
 * @param cexId - CEX ID (e.g., "binance", "kucoin")
 * @returns Data URL with embedded SVG, or DEFAULT_TOKEN_ICON if not found
 */
export const getCexLogo = (cexId: string): string => {
  const svgContent = EXCHANGE_LOGOS[cexId];
  if (!svgContent) return DEFAULT_TOKEN_ICON;
  const encodedSvg = encodeURIComponent(svgContent);
  return `data:image/svg+xml,${encodedSvg}`;
};

/**
 * Get the logo for any exchange (CEX or DEX)
 * Automatically detects exchange type and returns appropriate logo
 * @param exchangeId - Exchange ID (CEX like "binance" or DEX like "aerodrome-cl|base", "pancake-v3|arbitrum")
 * @returns Logo URL (data URL for CEX, path for DEX), or DEFAULT_TOKEN_ICON if not found
 */
export const getExchangeLogo = (exchangeId: string): string => {
  // Force value for vitest test
  if (process?.env?.VITEST_WORKER_ID) return exchangeId.toLowerCase().replace(".", "") + ".svg";
  if (!exchangeId) return DEFAULT_TOKEN_ICON;

  // Handle DEX IDs (format: {protocol}|{chain})
  if (isDexId(exchangeId)) {
    return getDexLogo(exchangeId) || DEFAULT_TOKEN_ICON;
  }

  // Handle CEX IDs
  return getCexLogo(exchangeId);
};
